import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { SearchableMultiSelect } from '@/components/ui/searchable-multiselect'
import { useAuthStore } from '@/store/auth.store'
import { attendanceService } from '@/services/attendanceService'
import { employeesService } from '@/services/employeesService'
import { shiftService } from '@/services/shiftService'
import type { Employee } from '@/api/types'
import type { DepartmentLookup } from '@/api/types'
import type { ShiftListItem } from '@/api/types'
import { formatEmployeeDisplayName } from '@/lib/employeeName'

function toUTCISO(dateStr: string, timeHHmm: string): string {
  if (!dateStr || !timeHHmm || timeHHmm.length < 5) return ''
  const local = `${dateStr}T${timeHHmm}:00`
  return new Date(local).toISOString()
}

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function getStartEndForMonth(ym: string): { startDate: string; endDate: string } {
  const pad = (n: number) => String(n).padStart(2, '0')
  if (!ym || ym.length < 7) {
    const d = new Date()
    const y = d.getFullYear()
    const m = d.getMonth() + 1
    const lastDay = new Date(y, m, 0).getDate()
    return {
      startDate: `${y}-${pad(m)}-01`,
      endDate: `${y}-${pad(m)}-${pad(lastDay)}`,
    }
  }
  const [y, m] = ym.split('-').map(Number)
  const lastDay = new Date(y, m, 0).getDate()
  return {
    startDate: `${y}-${pad(m)}-01`,
    endDate: `${y}-${pad(m)}-${pad(lastDay)}`,
  }
}

export const AttendanceAddCombined: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [saving, setSaving] = useState(false)
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [employeesList, setEmployeesList] = useState<Employee[]>([])
  const [shifts, setShifts] = useState<ShiftListItem[]>([])

  const [allDepartments, setAllDepartments] = useState(false)
  const [allEmployees, setAllEmployees] = useState(false)
  const [combinedDepartmentIds, setCombinedDepartmentIds] = useState<string[]>([])
  const [combinedEmployeeIds, setCombinedEmployeeIds] = useState<string[]>([])
  const [combinedShiftId, setCombinedShiftId] = useState<number>(0)
  const [combinedStatus, setCombinedStatus] = useState(1)
  const [monthYear, setMonthYear] = useState(getDefaultMonthYear)
  const monthRange = useMemo(() => getStartEndForMonth(monthYear), [monthYear])
  const [combinedDateFrom, setCombinedDateFrom] = useState(monthRange.startDate)
  const [combinedDateTo, setCombinedDateTo] = useState(monthRange.endDate)
  const [combinedTimeIn, setCombinedTimeIn] = useState('09:00')
  const [combinedTimeOut, setCombinedTimeOut] = useState('17:00')
  const [combinedWorkingHours, setCombinedWorkingHours] = useState(8)
  const [combinedLateHours, setCombinedLateHours] = useState(0)
  const [combinedIncompleteHours, setCombinedIncompleteHours] = useState(0)

  useEffect(() => {
    setCombinedDateFrom(monthRange.startDate)
    setCombinedDateTo(monthRange.endDate)
  }, [monthRange.startDate, monthRange.endDate])

  useEffect(() => {
    if (!branchId) {
      setDepartments([])
      return
    }
    let cancelled = false
    employeesService.getDepartmentLookups().then((list) => {
      if (!cancelled && list) setDepartments(list)
    }).catch(() => { if (!cancelled) setDepartments([]) })
    return () => { cancelled = true }
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setEmployeesList([])
      return
    }
    let cancelled = false
    employeesService.getAllEmployees(branchId, 1, 500).then((res) => {
      if (!cancelled && res?.data) setEmployeesList(res.data)
    }).catch(() => { if (!cancelled) setEmployeesList([]) })
    return () => { cancelled = true }
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setShifts([])
      return
    }
    let cancelled = false
    shiftService.getAll(branchId, 1, 200).then((res) => {
      if (!cancelled && res?.data) setShifts(res.data)
    }).catch(() => { if (!cancelled) setShifts([]) })
    return () => { cancelled = true }
  }, [branchId])

  const departmentOptionsForCombined = useMemo(() =>
    departments.filter((d) => d.id).map((d) => ({
      value: String(d.id),
      label: d.lookupNameEn || d.lookupNameAr || String(d.id),
    })), [departments])

  const employeesForCombinedByDepts = useMemo(() => {
    if (allDepartments || combinedDepartmentIds.length === 0) return employeesList
    const deptSet = new Set(combinedDepartmentIds.map(Number))
    return employeesList.filter((e) => e.empDepartmetId != null && deptSet.has(e.empDepartmetId))
  }, [employeesList, allDepartments, combinedDepartmentIds])

  const employeeOptionsForCombined = useMemo(() => {
    const seen = new Set<number>()
    return employeesForCombinedByDepts
      .filter((emp) => {
        if (!emp.id || !emp.empCode || seen.has(emp.id)) return false
        seen.add(emp.id)
        return true
      })
      .map((emp) => ({
        value: String(emp.id),
        label: formatEmployeeDisplayName(emp) || emp.empCode || '—',
      }))
  }, [employeesForCombinedByDepts])

  const shiftOptions = useMemo(() =>
    shifts.filter((s) => s.id != null).map((s) => ({
      value: String(s.id),
      label: s.name ?? s.shiftType ?? s.shiftDescription ?? s.description ?? t('attendancePage.shiftFallback', { id: s.id }),
    })), [shifts, t])

  const handleSave = async () => {
    if (!branchId) {
      toast.error(t('attendancePage.toastBranch'))
      return
    }
    const deptIds: number[] = allDepartments
      ? departments.filter((d) => d.id).map((d) => d.id!)
      : combinedDepartmentIds.map(Number).filter(Boolean)
    const empIds: number[] = allEmployees
      ? employeesForCombinedByDepts.filter((e) => e.id).map((e) => e.id!)
      : combinedEmployeeIds.map(Number).filter(Boolean)
    if (deptIds.length === 0 && !allDepartments) {
      toast.error(t('attendancePage.toastSelectDeptOrAll'))
      return
    }
    if (empIds.length === 0 && !allEmployees) {
      toast.error(t('attendancePage.toastSelectEmpOrAll'))
      return
    }
    const dateStr = combinedDateFrom || new Date().toISOString().slice(0, 10)
    const timeInISO = toUTCISO(dateStr, combinedTimeIn) || new Date().toISOString()
    const timeOutISO = toUTCISO(dateStr, combinedTimeOut) || new Date().toISOString()

    try {
      const existing = await attendanceService.getListByFilters(branchId, {
        departmentId: 0,
        empId: 0,
        dateFrom: combinedDateFrom || dateStr,
        dateTo: combinedDateTo || dateStr,
        pageNumber: 1,
        pageSize: 5000,
      })
      const existingCodes = new Set(
        (existing.data || []).map((r) => (r.employeeCode ?? '').trim()).filter(Boolean)
      )
      const employeesBeingAdded = employeesForCombinedByDepts.filter((e) => empIds.includes(e.id!))
      const hasOverlap = employeesBeingAdded.some(
        (emp) => emp.empCode && existingCodes.has(emp.empCode.trim())
      )
      if (hasOverlap) {
        await showError('Validation', 'Attendance already exists for one or more employees in the selected date range.')
        return
      }
    } catch (_) {
      // proceed with save if check fails (e.g. network)
    }

    setSaving(true)
    try {
      const payload = {
        departments: deptIds,
        employeeIds: empIds,
        attendanceStatus: combinedStatus,
        dateFrom: combinedDateFrom ? `${combinedDateFrom}T00:00:00.000Z` : timeInISO,
        dateTo: combinedDateTo ? `${combinedDateTo}T23:59:59.999Z` : timeOutISO,
        timeIn: timeInISO,
        timeOut: timeOutISO,
        workingHours: combinedWorkingHours,
        lateHours: combinedLateHours,
        incompleteHours: combinedIncompleteHours,
        branchId,
      }
      console.log('[Attendance Combined] API payload:', JSON.stringify(payload, null, 2))
      await attendanceService.addCombinedAttendanceBatch(payload)
      await showSuccess(t('attendancePage.savedTitle'), t('attendancePage.combinedSavedMessage'))
      navigate('/payroll/attendance')
    } catch (e: any) {
      await showError(t('attendancePage.saveFailedTitle'), e?.response?.data?.message ?? e?.response?.data?.msg ?? e?.response?.data?.result?.msg ?? e?.message ?? t('attendancePage.saveFailedMessage'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('attendancePage.combinedTitle')}
        description={t('attendancePage.combinedDescription')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('attendancePage.combinedCardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('attendancePage.labelDepartments')}</Label>
              <SearchableMultiSelect
                options={departmentOptionsForCombined}
                value={allDepartments ? ['__all_depts__'] : combinedDepartmentIds}
                onValueChange={(v) => {
                  if (v.includes('__all_depts__')) {
                    setAllDepartments(true)
                    setCombinedDepartmentIds([])
                  } else {
                    setAllDepartments(false)
                    setCombinedDepartmentIds(v)
                  }
                }}
                placeholder={t('attendancePage.placeholderSelectDepartments')}
                searchPlaceholder={t('attendancePage.searchShort')}
                emptyMessage={t('attendancePage.emptyDepts')}
                allOption={{ value: '__all_depts__', label: t('attendancePage.allDepartments') }}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelEmployees')}</Label>
              <SearchableMultiSelect
                options={employeeOptionsForCombined}
                value={allEmployees ? ['__all_emps__'] : combinedEmployeeIds}
                onValueChange={(v) => {
                  if (v.includes('__all_emps__')) {
                    setAllEmployees(true)
                    setCombinedEmployeeIds([])
                  } else {
                    setAllEmployees(false)
                    setCombinedEmployeeIds(v)
                  }
                }}
                placeholder={t('attendancePage.placeholderSelectEmployees')}
                searchPlaceholder={t('attendancePage.searchShort')}
                emptyMessage={t('attendancePage.emptyEmps')}
                allOption={{ value: '__all_emps__', label: t('attendancePage.allEmployees') }}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelShift')}</Label>
              <SearchableSelect
                options={shiftOptions}
                value={combinedShiftId ? String(combinedShiftId) : ''}
                onValueChange={(v) => setCombinedShiftId(v ? Number(v) : 0)}
                placeholder={t('attendancePage.placeholderSelectShift')}
                searchPlaceholder={t('attendancePage.searchShort')}
                emptyMessage={t('attendancePage.emptyShift')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelStatus')}</Label>
              <Select value={String(combinedStatus)} onValueChange={(v) => setCombinedStatus(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t('attendancePage.present')}</SelectItem>
                  <SelectItem value="0">{t('attendancePage.absent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelMonthYear')}</Label>
              <Input type="month" value={monthYear} onChange={(e) => setMonthYear(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.dateFrom')}</Label>
              <Input type="date" value={combinedDateFrom} onChange={(e) => setCombinedDateFrom(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.dateTo')}</Label>
              <Input type="date" value={combinedDateTo} onChange={(e) => setCombinedDateTo(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelTimeIn')}</Label>
              <Input type="time" value={combinedTimeIn} onChange={(e) => setCombinedTimeIn(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelTimeOut')}</Label>
              <Input type="time" value={combinedTimeOut} onChange={(e) => setCombinedTimeOut(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelWorkingHours')}</Label>
              <Input type="number" value={combinedWorkingHours} onChange={(e) => setCombinedWorkingHours(Number(e.target.value) || 0)} />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelLateHours')}</Label>
              <Input type="number" value={combinedLateHours} onChange={(e) => setCombinedLateHours(Number(e.target.value) || 0)} />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelUncompletedHours')}</Label>
              <Input type="number" value={combinedIncompleteHours} onChange={(e) => setCombinedIncompleteHours(Number(e.target.value) || 0)} />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? t('attendancePage.saving') : t('attendancePage.save')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/payroll/attendance')}>
              {t('attendancePage.cancel')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
